import { useMutation, useQueryClient } from '@tanstack/react-query';

import {
  request,
  type V3ApiFailedResponse,
  type V3ApiSuccessfulResponse,
} from 'javascripts/services/v3ApiClient';
import { ProjectDatabaseId } from 'data/v1';
import type { TaskObject } from 'data/v3/data-contracts';
import { PROJECT_TASKS_QUERY_KEY } from './useGetProjectTasks';
import {
  insertTaskAndReindex,
  removeTaskandReindex,
} from 'components/projects/projectTasks/hooks/helpers';

type UpdateProjectTaskPositionResponse = V3ApiSuccessfulResponse<TaskObject>;

type UpdateProjectTaskPositionParams = {
  task: {
    id: TaskObject['id'];
    details: string;
    position: number;
    status: string;
    uuid?: string;
  };
};

export const useUpdateProjectTaskPosition = ({
  projectDbId,
  onSuccess,
  onError,
}: {
  taskDbId: string;
  projectDbId: ProjectDatabaseId;
  onSuccess?: ({ updatedTask }: { updatedTask: TaskObject }) => void;
  onError?: () => void;
}) => {
  const queryClient = useQueryClient();

  return useMutation<
    UpdateProjectTaskPositionResponse,
    V3ApiFailedResponse,
    UpdateProjectTaskPositionParams
  >({
    mutationFn: async (params: UpdateProjectTaskPositionParams) => {
      const { id } = params.task;
      return request({
        url: `/projects/${projectDbId}/tasks/${id}`,
        method: 'put',
        data: params,
      });
    },
    onMutate: async (params: UpdateProjectTaskPositionParams) => {
      const { id: taskDbId, position: updatedPosition } = params.task;

      await queryClient.cancelQueries({
        queryKey: [PROJECT_TASKS_QUERY_KEY, projectDbId],
      });

      const projectTasksData = queryClient.getQueryData<any>([
        PROJECT_TASKS_QUERY_KEY,
        projectDbId,
      ]);

      if (projectTasksData) {
        const existingTasks = projectTasksData.data.tasks || [];

        let updatedTasks = existingTasks.map((task: TaskObject) => {
          if (task.id === taskDbId) {
            return params.task;
          }
          return task;
        });

        const removeIndex = existingTasks.findIndex(
          (task: TaskObject) => task.id === taskDbId,
        );

        const tasksWithoutUpdatedTask = removeTaskandReindex({
          tasks: existingTasks,
          removeIndex,
        });

        const insertIndex = tasksWithoutUpdatedTask.findIndex(
          (task: TaskObject) => task.position === updatedPosition,
        );

        updatedTasks = insertTaskAndReindex({
          updatedTask: params.task,
          insertIndex,
          tasks: tasksWithoutUpdatedTask,
        });

        queryClient.setQueryData([PROJECT_TASKS_QUERY_KEY, projectDbId], {
          data: {
            ...projectTasksData.data,
            tasks: updatedTasks,
          },
        });
      }
    },
    onSuccess: ({ data }: { data: TaskObject }) => {
      onSuccess?.({
        updatedTask: data,
      });
    },
    onError: () => {
      onError?.();
    },
    onSettled: () => {
      queryClient.invalidateQueries({
        queryKey: [PROJECT_TASKS_QUERY_KEY, projectDbId],
      });
    },
  });
};

export default useUpdateProjectTaskPosition;
